import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { isIntegrationLimited } from '@/hooks/integrations/google';
import { useGmailStatus } from '@/hooks/integrations/google/useGmailStatus';
import { useMicrosoftOAuthConnect } from '@/hooks/integrations/microsoft';
import { useOutlookStatus } from '@/hooks/integrations/microsoft/useOutlookStatus';
import { useGoogleOAuthConnect } from '@/hooks/useGoogleOAuthConnect';
import { AlertFullWidth } from '@/lib/base/overlays/alerts';
import { isTransientError } from '@/lib/errors';
import { getUnavailableCapabilities } from '@/lib/integrations/scopeCapabilities';

const CAPABILITY_LABELS: Record<string, string> = {
  Gmail: 'email sync',
  Calendar: 'calendar sync',
  Meet: 'Meet',
};

const PARTIAL_SCOPE_DISMISS_KEY_PREFIX = 'dismissed_partial_scopes';

/** Fallback label for accounts whose email is null/empty, so banner copy
 * stays readable (e.g. "Your account needs to be reconnected") instead of
 * starting with a blank string. */
const FALLBACK_ACCOUNT_LABEL = 'Your account';

/** Compose a display label for an account, falling back to a generic label
 * when the email is null, undefined, or blank/whitespace. A bare `?? FALLBACK`
 * would let an empty-string email through, leaving banner copy that starts with
 * nothing (e.g. " needs to be reconnected"). */
function accountLabel(email: string | null | undefined): string {
  return email?.trim() || FALLBACK_ACCOUNT_LABEL;
}

function formatInactiveCapabilities(
  accounts: { missing_scopes?: string[] }[]
): string | null {
  const allMissing = accounts.flatMap(acc => acc.missing_scopes ?? []);
  const capabilities = getUnavailableCapabilities(allMissing);
  if (capabilities.size === 0) {
    return null;
  }
  const labels = [...capabilities].map(
    cap => CAPABILITY_LABELS[cap] ?? cap.toLowerCase()
  );
  if (labels.length === 1) {
    return labels[0];
  }
  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

function formatAccountList(accounts: { email: string | null }[]): string {
  if (accounts.length === 0) {
    return '';
  }
  if (accounts.length === 1) {
    return accountLabel(accounts[0].email);
  }
  if (accounts.length === 2) {
    return `${accountLabel(accounts[0].email)} and ${accountLabel(accounts[1].email)}`;
  }
  return `${accountLabel(accounts[0].email)} and ${accounts.length - 1} other accounts`;
}

export function DisconnectedAccountBanner() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { connect: googleConnect, isConnecting: googleIsConnecting } =
    useGoogleOAuthConnect(orgId);
  const { connect: microsoftConnect, isConnecting: microsoftIsConnecting } =
    useMicrosoftOAuthConnect(orgId);

  // Google status
  const {
    data: gmailData,
    isLoading: gmailLoading,
    error: gmailError,
  } = useGmailStatus(orgId, { enabled: !!orgId });

  // Microsoft status
  const {
    data: outlookData,
    isLoading: outlookLoading,
    error: outlookError,
  } = useOutlookStatus(orgId);

  // --- Google disconnected accounts ---
  const googleDisconnected = useMemo(() => {
    if (!gmailData?.accounts) {
      return [];
    }
    return gmailData.accounts.filter(acc => acc.needs_reconnect);
  }, [gmailData]);

  // --- Microsoft disconnected accounts ---
  const microsoftDisconnected = useMemo(() => {
    if (!outlookData?.accounts) {
      return [];
    }
    return outlookData.accounts.filter(acc => acc.needs_reconnect);
  }, [outlookData]);

  // --- Google partial-scope accounts (Microsoft doesn't have partial scopes) ---
  const partialScopeAccounts = useMemo(() => {
    if (!gmailData?.accounts) {
      return [];
    }
    return gmailData.accounts.filter(
      acc =>
        isIntegrationLimited(acc.connection_status) &&
        (acc.missing_scopes?.length ?? 0) > 0
    );
  }, [gmailData]);

  // Fingerprint of the current partial-scope state so dismissal resets when
  // the set of affected accounts or missing scopes changes.
  const partialScopeFingerprint = useMemo(() => {
    if (partialScopeAccounts.length === 0) {
      return null;
    }
    const parts = partialScopeAccounts
      .map(
        acc =>
          `${acc.social_account_id ?? acc.email ?? ''}:${[...(acc.missing_scopes ?? [])].sort().join(',')}`
      )
      .sort()
      .join('|');
    return parts;
  }, [partialScopeAccounts]);

  const [dismissedFingerprint, setDismissedFingerprint] = useState<
    string | null
  >(() => {
    if (!orgId) {
      return null;
    }
    try {
      return localStorage.getItem(
        `${PARTIAL_SCOPE_DISMISS_KEY_PREFIX}_${orgId}`
      );
    } catch {
      return null;
    }
  });

  // Re-load dismissed state when orgId changes (initializer only runs once).
  useEffect(() => {
    if (!orgId) {
      setDismissedFingerprint(null);
      return;
    }
    try {
      setDismissedFingerprint(
        localStorage.getItem(`${PARTIAL_SCOPE_DISMISS_KEY_PREFIX}_${orgId}`)
      );
    } catch {
      setDismissedFingerprint(null);
    }
  }, [orgId]);

  const isPartialScopeDismissed =
    partialScopeFingerprint !== null &&
    dismissedFingerprint === partialScopeFingerprint;

  const hasGoogleError = !!gmailError && !isTransientError(gmailError);
  const hasMicrosoftError = !!outlookError && !isTransientError(outlookError);

  // --- Handlers ---
  const handleGoogleReconnect = useCallback(() => {
    if (hasGoogleError) {
      navigate(`/${orgId}/settings/integrations`);
    } else {
      googleConnect();
    }
  }, [hasGoogleError, navigate, orgId, googleConnect]);

  const handleMicrosoftReconnect = useCallback(() => {
    if (hasMicrosoftError) {
      navigate(`/${orgId}/settings/integrations`);
    } else {
      microsoftConnect();
    }
  }, [hasMicrosoftError, navigate, orgId, microsoftConnect]);

  const handleUpdatePermissions = useCallback(() => {
    googleConnect();
  }, [googleConnect]);

  const handleDismissPartialScopes = useCallback(() => {
    if (!orgId || !partialScopeFingerprint) {
      return;
    }
    try {
      localStorage.setItem(
        `${PARTIAL_SCOPE_DISMISS_KEY_PREFIX}_${orgId}`,
        partialScopeFingerprint
      );
    } catch {
      // Ignore storage errors (private browsing, quota exceeded)
    }
    setDismissedFingerprint(partialScopeFingerprint);
  }, [orgId, partialScopeFingerprint]);

  // Don't render during initial load
  if (gmailLoading || outlookLoading) {
    return null;
  }

  // --- Google alert ---
  const showGoogleAlert = hasGoogleError || googleDisconnected.length > 0;
  let googleDescription: string | undefined;
  let googleButtonLabel: string | undefined;
  if (showGoogleAlert) {
    const accountText = hasGoogleError
      ? 'One or more accounts may need attention'
      : formatAccountList(googleDisconnected);
    googleDescription = hasGoogleError
      ? accountText
      : `${accountText} needs to be reconnected to continue syncing.`;
    if (hasGoogleError) {
      googleButtonLabel = 'Go to settings';
    } else if (googleIsConnecting) {
      googleButtonLabel = 'Connecting…';
    } else {
      googleButtonLabel = 'Reconnect';
    }
  }

  // --- Microsoft alert ---
  const showMicrosoftAlert =
    hasMicrosoftError || microsoftDisconnected.length > 0;
  let microsoftDescription: string | undefined;
  let microsoftButtonLabel: string | undefined;
  if (showMicrosoftAlert) {
    const accountText = hasMicrosoftError
      ? 'One or more accounts may need attention'
      : formatAccountList(microsoftDisconnected);
    microsoftDescription = hasMicrosoftError
      ? accountText
      : `${accountText} needs to be reconnected to continue syncing.`;
    if (hasMicrosoftError) {
      microsoftButtonLabel = 'Go to settings';
    } else if (microsoftIsConnecting) {
      microsoftButtonLabel = 'Connecting…';
    } else {
      microsoftButtonLabel = 'Reconnect';
    }
  }

  // --- Partial scopes (Google-only) ---
  const inactiveCapabilities = formatInactiveCapabilities(partialScopeAccounts);

  let partialScopeDescription: string | null = null;
  if (inactiveCapabilities && partialScopeAccounts.length === 1) {
    partialScopeDescription = `${accountLabel(partialScopeAccounts[0].email)} is connected, but a required Google permission wasn't granted.`;
  } else if (inactiveCapabilities && partialScopeAccounts.length === 2) {
    partialScopeDescription = `${accountLabel(partialScopeAccounts[0].email)} and ${accountLabel(partialScopeAccounts[1].email)} are connected, but a required Google permission wasn't granted.`;
  } else if (inactiveCapabilities && partialScopeAccounts.length > 2) {
    partialScopeDescription = `${accountLabel(partialScopeAccounts[0].email)} and ${partialScopeAccounts.length - 1} other accounts are connected, but a required Google permission wasn't granted.`;
  }

  const partialScopeTitle = inactiveCapabilities
    ? `${inactiveCapabilities.charAt(0).toUpperCase()}${inactiveCapabilities.slice(1)} inactive`
    : null;

  const showPartialScopeAlert =
    partialScopeAccounts.length > 0 && partialScopeTitle != null;

  if (!showGoogleAlert && !showMicrosoftAlert && !showPartialScopeAlert) {
    return null;
  }

  return (
    <>
      {showGoogleAlert && googleDescription && googleButtonLabel && (
        <AlertFullWidth
          title='Google account disconnected'
          description={googleDescription}
          color='warning'
          stackContent={true}
          confirmLabel={googleButtonLabel}
          onConfirm={handleGoogleReconnect}
          actionType='button'
        />
      )}
      {showMicrosoftAlert && microsoftDescription && microsoftButtonLabel && (
        <AlertFullWidth
          title='Microsoft account disconnected'
          description={microsoftDescription}
          color='warning'
          stackContent={true}
          confirmLabel={microsoftButtonLabel}
          onConfirm={handleMicrosoftReconnect}
          actionType='button'
        />
      )}
      {showPartialScopeAlert &&
        partialScopeDescription &&
        !isPartialScopeDismissed && (
          <AlertFullWidth
            title={partialScopeTitle}
            description={partialScopeDescription}
            color='warning'
            stackContent={true}
            confirmLabel={
              googleIsConnecting ? 'Connecting…' : 'Grant permission'
            }
            onConfirm={handleUpdatePermissions}
            onClose={handleDismissPartialScopes}
            actionType='button'
          />
        )}
    </>
  );
}
